import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  ChiliPiperMeeting,
  ContactFormFields,
} from '@transcend-io/contact-form-schema';

import { useAnalytics } from '../segment';
import { mergeChiliPiperMeetings, parseChiliPiperMeeting } from './meeting';
import { ChiliPiperScript } from './script';
import { triggerSchedulingModal } from './trigger';

export enum SchedulingStateEnum {
  IDLE = 'idle',
  ROUTING = 'routing',
  COMPLETE = 'complete',
  CLOSED = 'closed',
}

type SchedulingState = {
  status: SchedulingStateEnum;
  showModal: boolean;
};

interface ChiliPiperContextType {
  schedulingState: SchedulingState;
  setSchedulingState: React.Dispatch<React.SetStateAction<SchedulingState>>;
  initSchedulingModal: (fields: ContactFormFields, analyticsId: string) => void;
  waitForSchedulingLoad: () => Promise<void>;
  /**
   * Resolves after ChiliPiper has routed, disqualified, booked, or closed.
   * If a calendar is showing, waits until the user books or closes it.
   * Returns the booked meeting when Concierge confirmed one.
   */
  waitForSchedulingFinished: () => Promise<ChiliPiperMeeting | null>;
  bookedMeeting: ChiliPiperMeeting | null;
  handleCloseModal: () => void;
}

const ChiliPiperContext = createContext<ChiliPiperContextType | undefined>(
  undefined,
);

interface DeferredPromise {
  resolve: () => void;
  promise: Promise<void>;
}

function createDeferredPromise(): DeferredPromise {
  let resolve!: () => void;
  const promise = new Promise<void>((r) => {
    resolve = r;
  });
  return { resolve, promise };
}

export function ChiliPiperProvider({
  children,
  enableScheduling,
  routerId,
}: {
  children: ReactNode;
  enableScheduling: boolean;
  routerId?: string | null;
}) {
  const [schedulingState, setSchedulingState] = useState<SchedulingState>({
    status: SchedulingStateEnum.IDLE,
    showModal: false,
  });
  const analytics = useAnalytics();

  const schedulingPromiseRef = useRef<DeferredPromise | undefined>(undefined);
  const finishedPromiseRef = useRef<DeferredPromise | undefined>(undefined);
  const fieldsRef = useRef<ContactFormFields | undefined>(undefined);
  const analyticsIdRef = useRef<string | undefined>(undefined);
  const hasTrackedModalRef = useRef(false);
  const hasOpenedRef = useRef(false);
  const bookedMeetingRef = useRef<ChiliPiperMeeting | null>(null);
  const [bookedMeeting, setBookedMeeting] = useState<ChiliPiperMeeting | null>(
    null,
  );

  const recordMeeting = (incoming: ChiliPiperMeeting | null) => {
    if (!incoming) {
      return;
    }
    const next = mergeChiliPiperMeetings(bookedMeetingRef.current, incoming);
    bookedMeetingRef.current = next;
    setBookedMeeting(next);
  };

  const resolveFinished = () => {
    finishedPromiseRef.current?.resolve();
  };

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'object' || !event.data) return;

      const { action } = event.data;
      if (
        typeof action === 'string' &&
        process.env.NODE_ENV === 'development'
      ) {
        console.info('[ChiliPiper] message', event.data);
      }
      if (action === 'booked' || action === 'booking-confirmed') {
        recordMeeting(parseChiliPiperMeeting(event.data));
      }
      if (action === 'availability-loaded') {
        const fields = fieldsRef.current;
        if (fields && !hasTrackedModalRef.current) {
          hasTrackedModalRef.current = true;
          analytics.track('Booking Modal Shown', {
            email: fields.email,
            formId: analyticsIdRef.current,
            submissionId: fields.submissionId,
            source: 'Chili Piper',
          });
        }
      }
    };

    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage, false);
  }, [analytics]);

  const handleRouted = () => {
    setSchedulingState({
      status: SchedulingStateEnum.COMPLETE,
      showModal: true,
    });
    schedulingPromiseRef.current?.resolve();
  };

  const handleDisqualified = () => {
    hasOpenedRef.current = false;
    setSchedulingState({
      status: SchedulingStateEnum.COMPLETE,
      showModal: false,
    });
    schedulingPromiseRef.current?.resolve();
    resolveFinished();
  };

  const handleCloseModal = () => {
    hasOpenedRef.current = false;
    setSchedulingState({
      status: SchedulingStateEnum.CLOSED,
      showModal: false,
    });
    resolveFinished();
  };

  const handleSuccess = (data?: unknown) => {
    recordMeeting(parseChiliPiperMeeting(data));
    hasOpenedRef.current = false;
    setSchedulingState({
      status: SchedulingStateEnum.CLOSED,
      showModal: false,
    });
    schedulingPromiseRef.current?.resolve();
    resolveFinished();
  };

  const handleError = (data?: unknown) => {
    console.error('ChiliPiper routing error', data);
    hasOpenedRef.current = false;
    setSchedulingState({
      status: SchedulingStateEnum.COMPLETE,
      showModal: false,
    });
    schedulingPromiseRef.current?.resolve();
    resolveFinished();
  };

  const initSchedulingModal = useCallback(
    (fields: ContactFormFields, analyticsId: string) => {
      if (hasOpenedRef.current) {
        return;
      }
      if (!routerId) {
        console.error('ChiliPiper router ID is missing');
        return;
      }
      hasOpenedRef.current = true;
      bookedMeetingRef.current = null;
      setBookedMeeting(null);
      fieldsRef.current = fields;
      analyticsIdRef.current = analyticsId;
      hasTrackedModalRef.current = false;
      schedulingPromiseRef.current = createDeferredPromise();
      finishedPromiseRef.current = createDeferredPromise();
      setSchedulingState({
        status: SchedulingStateEnum.ROUTING,
        showModal: false,
      });
      triggerSchedulingModal({
        fields,
        routerId,
        onClose: handleCloseModal,
        onDisqualified: handleDisqualified,
        onRouted: handleRouted,
        onSuccess: handleSuccess,
        onError: handleError,
      });
    },
    [routerId],
  );

  function waitForSchedulingLoad(): Promise<void> {
    if (!schedulingPromiseRef.current?.promise) {
      return Promise.resolve();
    }

    return Promise.race([
      schedulingPromiseRef.current.promise,
      new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 8000);
      }),
    ]);
  }

  /**
   * Resolves after the prospect books, closes, is disqualified, or routing
   * errors. Always waits for that terminal signal (not only when showModal
   * flipped) so a slow Concierge load cannot skip the meeting upsert.
   */
  async function waitForSchedulingFinished(): Promise<ChiliPiperMeeting | null> {
    await waitForSchedulingLoad();
    if (finishedPromiseRef.current?.promise) {
      await finishedPromiseRef.current.promise;
    }
    return bookedMeetingRef.current;
  }

  useEffect(() => {
    return () => {
      schedulingPromiseRef.current = undefined;
      finishedPromiseRef.current = undefined;
    };
  }, []);

  return (
    <ChiliPiperContext.Provider
      value={{
        schedulingState,
        setSchedulingState,
        initSchedulingModal,
        waitForSchedulingLoad,
        waitForSchedulingFinished,
        handleCloseModal,
        bookedMeeting,
      }}
    >
      {enableScheduling && <ChiliPiperScript />}
      {children}
    </ChiliPiperContext.Provider>
  );
}

export function useChiliPiperModal() {
  const context = useContext(ChiliPiperContext);
  if (context === undefined) {
    throw new Error('useChiliPiper must be used within a ChiliPiperProvider');
  }
  return context;
}
